import { useEffect } from "react";
import { usePrefersReducedMotion } from "./usePrefersReducedMotion";

/**
 * Drives every element carrying a `data-par` speed.
 *
 * Ported from the prototype: an element's offset from the centre of the
 * viewport, clamped to [-1, 1], scaled by its speed. rAF-throttled behind a
 * single passive scroll listener rather than one per element.
 *
 * The prototype ran this unconditionally; here it is off under
 * prefers-reduced-motion, as the handoff asks for.
 */
export function useParallax(): void {
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const layers = Array.from(document.querySelectorAll<HTMLElement>("[data-par]"));

    if (reducedMotion) {
      layers.forEach((el) => {
        el.style.transform = "";
      });
      return;
    }

    let ticking = false;

    const update = () => {
      const viewportHeight = window.innerHeight;
      for (const el of layers) {
        const speed = parseFloat(el.dataset.par ?? "0") || 0;
        const rect = el.getBoundingClientRect();
        const raw = (rect.top + rect.height / 2 - viewportHeight / 2) / viewportHeight;
        const progress = Math.max(-1, Math.min(1, raw));
        el.style.transform = `translate3d(0, ${(-progress * speed * 160).toFixed(2)}px, 0)`;
      }
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    update();

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, [reducedMotion]);
}
